import { skipToken } from '@reduxjs/toolkit/query';
import { useGetPricePreviewQuery } from '../api/booking/pricePreviewApiSlice';
import { PricePreviewArgs } from '../api/booking/pricePreviewApiTypes';
import { PricePreviewStatus } from '../components/reservationSummary/PricePreviewStateRow';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * Shared price-preview wiring for the create/edit booking pages (#1203):
 * debounces the live form inputs, calls POST /admin/order/price-preview, and
 * derives the price-box state for the window where no breakdown is rendered.
 *
 * Pass `undefined` while the form inputs don't determine a price yet — no
 * request is sent and the status is 'idle'. The status is derived from the
 * undebounced args so the box switches to 'loading' the moment the form is
 * complete.
 *
 * 'error' only shows once a request has settled in error AND no newer request
 * is in flight. Intermediate form states can legitimately 400 (e.g. half-edited
 * date ranges); flashing the red row while the corrected call is already
 * running reads as a failure to the user even though it self-heals.
 */
export function usePricePreview(args: PricePreviewArgs | undefined) {
    const debouncedArgs = useDebouncedValue(args, 300);
    const { data: breakdown, isError, isFetching } = useGetPricePreviewQuery(debouncedArgs ?? skipToken);
    // args !== debouncedArgs covers the debounce window right after an input
    // change, before the follow-up request starts (isFetching still false).
    const errorSettled = isError && !isFetching && args === debouncedArgs;
    const previewStatus: PricePreviewStatus = errorSettled ? 'error' : args ? 'loading' : 'idle';
    return { breakdown, previewStatus };
}
